import React, { useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiExternalLink } from 'react-icons/fi';

const CalendarView = ({ applications }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Get current month and year
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  
  // Get days in month
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  
  // Get first day of month (0 = Sunday, 1 = Monday, etc.)
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
  
  // Create calendar days array
  const calendarDays = [];
  
  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push({ day: null, isCurrentMonth: false });
  }
  
  // Add days of the current month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push({ day, isCurrentMonth: true });
  }
  
  // Group applications by date
  const applicationsByDate = applications.reduce((acc, app) => {
    if (!app.appliedAt) return acc;
    const date = new Date(app.appliedAt);
    if (isNaN(date.getTime())) return acc;
    const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(app);
    return acc;
  }, {});
  
  // Navigate to previous month
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };
  
  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };
  
  // Format month name
  const monthName = currentDate.toLocaleString('default', { month: 'long' });
  
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <h2 className="text-lg font-medium text-gray-900">
          {monthName} {currentYear}
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={goToPreviousMonth}
            className="p-2 rounded-full hover:bg-gray-100"
            aria-label="Previous month"
          >
            <FiChevronLeft className="h-5 w-5 text-gray-600" />
          </button>
          <button
            onClick={goToNextMonth}
            className="p-2 rounded-full hover:bg-gray-100"
            aria-label="Next month"
          >
            <FiChevronRight className="h-5 w-5 text-gray-600" />
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-7 gap-px bg-gray-200">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div key={day} className="bg-gray-50 py-2 text-center text-sm font-medium text-gray-500">
            {day}
          </div>
        ))}
        
        {calendarDays.map((day, index) => {
          const dateKey = day.isCurrentMonth 
            ? `${currentYear}-${currentMonth}-${day.day}` 
            : null;
          
          const dayApplications = dateKey ? applicationsByDate[dateKey] || [] : [];
          
          return (
            <div
              key={index}
              className={`min-h-[100px] p-2 ${
                day.isCurrentMonth ? 'bg-white' : 'bg-gray-50 text-gray-400'
              }`}
            >
              {day.day && (
                <>
                  <p className={`text-sm font-medium ${
                    new Date().getDate() === day.day &&
                    new Date().getMonth() === currentMonth &&
                    new Date().getFullYear() === currentYear
                      ? 'text-primary-600 bg-primary-100 rounded-full w-7 h-7 flex items-center justify-center'
                      : ''
                  }`}>
                    {day.day}
                  </p>
                  
                  <div className="mt-1 space-y-1 max-h-[80px] overflow-y-auto">
                    {dayApplications.map((app) => (
                      <div
                        key={app.id}
                        className="text-xs p-1 rounded truncate flex items-center justify-between cursor-pointer hover:opacity-80"
                        style={{
                          backgroundColor: 
                            app.status === 'applied' ? '#DBEAFE' :
                            app.status === 'interviewing' ? '#FEF3C7' :
                            app.status === 'offered' ? '#D1FAE5' :
                            '#FEE2E2'
                        }}
                        title={`${app.company} - ${app.jobTitle}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          app.onView(app);
                        }}
                      >
                        <span className="truncate">{app.company}</span>
                        <FiExternalLink className="h-3 w-3 ml-1 flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarView;